import React, { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Button, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { useAppContext } from "../../Context/AppContext/AppContext";
import { useAuth } from "../../Context/AuthContext/AuthContext";
import AuthService from "../../Services/auth.service";
import { openNotification } from "../Notification/Index";
import { qualifymeSupportEmail } from "../../Utils/constants";
import "./Layout.scss";

export default function Layout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLElement>(null);
  const { organization } = useAppContext();
  const { isUserLoggedIn, user } = useAuth();

  const authService = AuthService.getInstance();

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [pathname]);

  const profileMenuItems: MenuProps["items"] = [
    { key: "profile", label: "Profile" },
    { key: "signout", label: "Sign out" },
  ];

  const handleProfileMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "profile") {
      navigate("/profile");
    }
    if (key === "signout") {
      authService
        .signOut()
        .then(() => {
          console.debug("User signed out successfully");
        })
        .catch((error) => {
          console.error("Error signing out:", error);
        });
    }
  };

  const handleSignIn = () => {
    navigate("/sign-in");
  };

  const handleOrganizationClick = () => {
    navigate("/", { replace: true });
  };

  const handleHelpClick = () => {
    const email = organization?.supportEmail
      ? organization.supportEmail
      : qualifymeSupportEmail;

    const subject = "Support Request";
    const mailtoLink = organization?.supportEmail
      ? `mailto:${email}?subject=${encodeURIComponent(subject)}&bcc=${qualifymeSupportEmail}`
      : `mailto:${email}?subject=${encodeURIComponent(subject)}`;

    let mailClientLikelyOpened = false;

    const handleBlur = () => {
      mailClientLikelyOpened = true;
      window.removeEventListener("blur", handleBlur);
    };

    window.addEventListener("blur", handleBlur);

    window.location.href = mailtoLink;

    setTimeout(() => {
      window.removeEventListener("blur", handleBlur);

      if (!mailClientLikelyOpened) {
        const fallbackGmailLink = organization?.supportEmail
          ? `https://mail.google.com/mail/?view=cm&to=${email}&bcc=${qualifymeSupportEmail}&su=${encodeURIComponent(
              subject,
            )}`
          : `https://mail.google.com/mail/?view=cm&to=${email}&su=${encodeURIComponent(
              subject,
            )}`;
        const newTab = window.open(fallbackGmailLink, "_blank");

        if (newTab) {
          newTab.focus();
        } else {
          openNotification(
            "warning",
            "Blocker detected",
            `Please allow pop-ups for this site or visit email app  and send email to ${email}.`,
          );
        }
      }
    }, 750);
  };

  const orgLogoUrl =
    organization && organization.logo
      ? `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/organization/${organization?._id}/${organization?.logo}`
      : "/images/qualifyme_light.png";

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="app-header__inner">
          {organization && (
            <div className="app-header__left">
              <div className="app-header__brand">
                <img
                  className="app-header__logo"
                  src={orgLogoUrl}
                  alt="QualifyMe"
                  onClick={handleOrganizationClick}
                />
                <span
                  className="app-header__title"
                  onClick={handleOrganizationClick}
                >
                  {organization?.orgName}
                </span>
              </div>
            </div>
          )}
          <div className="app-header__right">
            <Button type="link" size="small" className="app-header__help-link" onClick={handleHelpClick}>
              Help
            </Button>
            {isUserLoggedIn && user ? (
              <Dropdown
                menu={{
                  items: profileMenuItems,
                  onClick: handleProfileMenuClick,
                }}
                trigger={["hover"]}
                placement="bottomRight"
              >
                <div className="app-header__user">
                  <div className="app-header__user-info">
                    <p className="app-header__user-name">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="app-header__user-role">Candidate</p>
                  </div>
                  <div className="app-header__avatar">
                    <Avatar
                      className="app-header__avatar-inner"
                      style={{
                        backgroundColor: "var(--ant-color-primary)",
                        color: "#fff",
                      }}
                    >
                      {[user?.firstName?.charAt(0), user?.lastName?.charAt(0)]
                        .filter(Boolean)
                        .join("")
                        .toUpperCase() || "?"}
                    </Avatar>
                  </div>
                </div>
              </Dropdown>
            ) : (
              <p className="app-header__help-link" onClick={handleSignIn}>
                Sign In
              </p>
            )}
          </div>
        </div>
      </header>
      <main ref={contentRef} className="app-content">
        <Outlet />
      </main>
    </div>
  );
}
